// @flow
import type { ID, AppState, Selector } from '../../../core/types'
import type { Charity, Contact } from '../../charity/types'
import type { Auction } from '../../auction/types'
import { createSelector } from 'reselect'
import { getById, getDomain } from '../../../modules/entities/'
import { hget } from '../../../modules/hash'
import {
  isCommCompleted,
  isCommInProgress
} from '../../../modules/communication'
import { domains } from '../../constants'
import { isAuctionClosed, isAuctionDraft } from '../../auction/utils'
import {
  CONTACT_FORM_MODAL,
  DELETE_CONTACT_MODAL,
  MYCHARITY_AUCTIONS_RESOURCE,
  MYCHARITY_RESOURCE
} from './constants'
import {
  ascendingAuctionsByCloseTimeSorter,
  descendingAuctionsByCloseTimeSorter
} from '../../utils'
export { getUserCharityId } from '../../session'

export const getCharity = (id: ID, state: AppState): Charity =>
  getById(domains.CHARITIES, id, state)

export const makeCharityWithContactsSelector = (id: ID): Selector<Charity> =>
  createSelector(
    getById(domains.CHARITIES, id),
    getDomain(domains.CONTACTS),
    (charity, allContacts) => {
      if (!charity) return null
      return {
        ...charity,
        contacts: (charity.contacts || []).map(
          contactId => allContacts[contactId]
        )
      }
    }
  )

export const isMyCharityLoaded = (state: AppState): boolean =>
  isCommCompleted(MYCHARITY_RESOURCE, state)

export const isMyCharityLoading = (state: AppState): boolean =>
  isCommInProgress(MYCHARITY_RESOURCE, state)

export const getAreMyAuctionsLoaded = (state: AppState): boolean =>
  isCommCompleted(MYCHARITY_AUCTIONS_RESOURCE, state)

export const getCharityUpcomingAuctions = (
  charityId: ID
): Selector<Auction[]> =>
  createSelector(
    getById(domains.CHARITIES, charityId),
    getDomain(domains.AUCTIONS),
    (charity: Charity, allAuctions: { [ID]: Auction }) =>
      (charity.auctions || [])
        .map(auctionId => allAuctions[auctionId])
        .filter(
          auction => !isAuctionDraft(auction) && !isAuctionClosed(auction)
        )
        .sort(ascendingAuctionsByCloseTimeSorter)
  )

export const getCharityFinishedAuctions = (
  charityId: ID
): Selector<Auction[]> =>
  createSelector(
    getById(domains.CHARITIES, charityId),
    getDomain(domains.AUCTIONS),
    (charity: Charity, allAuctions: { [ID]: Auction }) =>
      (charity.auctions || [])
        .map(auctionId => allAuctions[auctionId])
        .filter(auction => !isAuctionDraft(auction) && isAuctionClosed(auction))
        .sort(descendingAuctionsByCloseTimeSorter)
  )

export const getCharityDraftAuctions = (charityId: ID): Selector<Auction[]> =>
  createSelector(
    getById(domains.CHARITIES, charityId),
    getDomain(domains.AUCTIONS),
    (charity, allAuctions) => {
      return (charity.auctions || [])
        .map(auctionId => allAuctions[auctionId])
        .filter(auction => isAuctionDraft(auction))
    }
  )

export const isContactFormModalOpen: Selector<boolean> = hget(
  CONTACT_FORM_MODAL,
  'isModalOpen'
)

export const isEditingContactFormMode: Selector<boolean> = hget(
  CONTACT_FORM_MODAL,
  'isEditingMode'
)

export const getModalContactId = (modalName: string, state: AppState): ID =>
  hget(modalName, 'contactId', state)

export const getContact = (contactId: ID, state: AppState): Contact =>
  getById(domains.CONTACTS, contactId, state)

export const isDeleteContactModalOpen: Selector<boolean> = hget(
  DELETE_CONTACT_MODAL,
  'isModalOpen'
)



// WEBPACK FOOTER //
// ./src/services/mycharity/charity/selectors.js